import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import apiClient from '../services/api';
import { getAttributionPayload, getAttributionPayloadWithFingerprint, clearAttribution } from '../utils/attribution';

interface User {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
  is_active: boolean;
  balance: number;
  created_at: string;
  last_login?: string;
  oauth_provider: string;
  oauth_id?: string;
  reports_count?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  oauthLogin: (provider: string, oauthId: string, email: string, name: string) => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth validation on OAuth callback pages to prevent race condition:
      // checkAuth() could get a 401 for stale tokens and redirect to /login,
      // overriding the OAuth callback flow that's trying to set new tokens
      const path = window.location.pathname;
      if (path.startsWith('/auth/') || path.includes('/authorized')) {
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (token) {
        try {
          // The axios interceptor will handle token refresh automatically on 401
          const response = await authAPI.me();
          setUser(response.data);
          localStorage.setItem('isAdmin', response.data.is_admin ? 'true' : 'false');
        } catch (error) {
          console.error('Error checking authentication:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('isAdmin');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      localStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }

      // Get user info
      const userResponse = await authAPI.me();
      setUser(userResponse.data);
      localStorage.setItem('isAdmin', userResponse.data.is_admin ? 'true' : 'false');
      clearAttribution();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const oauthLogin = async (provider: string, oauthId: string, email: string, name: string) => {
    try {
      const response = await authAPI.oauthLogin(provider, oauthId, email, name);
      localStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }

      // Get user info
      const userResponse = await authAPI.me();
      setUser(userResponse.data);
      localStorage.setItem('isAdmin', userResponse.data.is_admin ? 'true' : 'false');
      clearAttribution();
    } catch (error) {
      console.error('OAuth login error:', error);
      throw error;
    }
  };

  const sendEmailLink = async (email: string) => {
    try {
      const attribution = await getAttributionPayloadWithFingerprint();
      const body = Object.keys(attribution).length > 0
        ? { email, attribution }
        : { email };
      await apiClient.post('/auth/email/login', body);
    } catch (error) {
      console.error('Email link error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Revoke refresh token on server (best effort)
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await apiClient.post('/auth/logout', { refresh_token: refreshToken });
      } catch (e) {
        // Ignore errors — still clear local state
        console.warn('Error revoking refresh token:', e);
      }
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('isAdmin');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.me();
      setUser(response.data);
      localStorage.setItem('isAdmin', response.data.is_admin ? 'true' : 'false');
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || false,
        isLoading,
        login,
        oauthLogin,
        sendEmailLink,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
